/** Prices are integer minor units. The existing Staffelpreise app owns every rule. */
export function normaliseTiers(rule, priceCents, currency) {
  if (!rule || rule.pricingType !== 'FIXED_PRICE' || !Array.isArray(rule.tiers)) return [];
  const unique = new Map();
  for (const tier of rule.tiers) {
    const minQuantity = Number(tier.minQuantity);
    const value = Number(tier.pricingValue ?? tier.fixedVpePriceCents);
    if (!Number.isSafeInteger(minQuantity) || minQuantity < 1 || !Number.isSafeInteger(value) || value <= 0 || value > priceCents) continue;
    if (tier.currency !== currency) continue;
    if (!unique.has(minQuantity) || unique.get(minQuantity) > value) unique.set(minQuantity, value);
  }
  return [...unique].map(([minQuantity, value]) => ({ minQuantity, priceCents: value })).sort((a, b) => a.minQuantity - b.minQuantity);
}

export function resolveTier(rules, priceCents, quantity, currency, now = Date.now()) {
  const count = Number.isFinite(Number(quantity)) ? Math.max(1, Math.floor(Number(quantity))) : 1;
  const candidates = (Array.isArray(rules) ? rules : []).filter((rule) => {
    if (!rule || rule.active === false || rule.customerTags?.length || Number(rule.minCartTotalCents || 0) > 0) return false;
    // The installed Staffelpreise Function's run.js uses currency codes in this
    // legacy field (line.currency), despite its name. Match checkout semantics.
    if (rule.marketHandles?.length && !rule.marketHandles.includes(currency)) return false;
    if (rule.startsAt && (!Number.isFinite(Date.parse(rule.startsAt)) || Date.parse(rule.startsAt) > now)) return false;
    if (rule.endsAt && (!Number.isFinite(Date.parse(rule.endsAt)) || Date.parse(rule.endsAt) < now)) return false;
    return normaliseTiers(rule, priceCents, currency).length > 0;
  }).sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0));
  // The Function tries lower-priority rules when a higher-priority rule has
  // no applicable discount at this quantity. Keep the first rule for the
  // informative ladder if no rule discounts the selected quantity.
  const rule = candidates.find((candidate) => {
    const tier = normaliseTiers(candidate, priceCents, currency).filter((item) => item.minQuantity <= count).at(-1);
    return tier && tier.priceCents < priceCents;
  }) || candidates[0] || null;
  const tiers = normaliseTiers(rule, priceCents, currency);
  const selected = tiers.filter((tier) => tier.minQuantity <= count).at(-1);
  const price = selected?.priceCents ?? priceCents;
  return { priceCents: price, totalCents: price * count, quantity: count, minQuantity: selected?.minQuantity || 1, rule, tiers };
}

function enhanceFloetrolVideos(root) {
  // Keep ordinary YouTube links as the no-JavaScript fallback, then use real
  // buttons for in-page playback. Link navigation handlers cannot intercept
  // these controls, and video setup is independent of commerce initialisation.
  root.querySelectorAll('a.fa-floetrol-video[data-fa-video]').forEach((link) => {
    const id = link.dataset.faVideo;
    if (!/^[A-Za-z0-9_-]{11}$/.test(id)) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = link.className;
    button.dataset.faVideo = id;
    button.dataset.faVideoControl = 'true';
    button.textContent = link.textContent.trim();
    button.style.width = '100%';
    button.style.border = '0';
    button.style.fontFamily = 'inherit';
    button.style.cursor = 'pointer';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
      frame.title = button.textContent.trim();
      frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.className = 'fa-floetrol-video-frame';
      button.replaceWith(frame);
      frame.focus();
    });
    link.replaceWith(button);
  });
}

function initFloetrol(root) {
  enhanceFloetrolVideos(root);
  if (root.dataset.faReady === 'true') return;
  let config;
  try { config = JSON.parse(root.querySelector('[data-fa-floetrol-config]').textContent); }
  catch { root.dataset.faInitError = 'config'; return; }
  const labels = config.labels;
  const quantity = root.querySelector('[data-fa-main-quantity]');
  const formatter = new Intl.NumberFormat(config.locale === 'de' ? 'de-CH' : `${config.locale}-CH`, { style: 'currency', currency: config.currency });
  const money = (value) => formatter.format(value / 100);
  const mainForm = root.querySelector('[data-fa-main-form]');
  const tierList = root.querySelector('[data-fa-tier-list]');
  if (!quantity || !mainForm || !tierList) { root.dataset.faInitError = 'commerce-elements'; return; }
  const setText = (selector, text) => root.querySelectorAll(selector).forEach((node) => { node.textContent = text; });

  function refreshPrice() {
    if (!quantity?.checkValidity()) return;
    const result = resolveTier(config.rules, config.priceCents, quantity.value, config.currency);
    setText('[data-fa-selected-price], [data-fa-sticky-price]', money(result.priceCents));
    setText('[data-fa-unit-price]', `${money(Math.round(result.priceCents / config.litres))} ${labels.perLitre}`);
    setText('[data-fa-total]', `${labels.total} ${money(result.totalCents)}`);
    tierList.querySelectorAll('[data-fa-tier-min]').forEach((button) => {
      const min = Number(button.dataset.faTierMin);
      const active = min === result.minQuantity;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  const initial = resolveTier(config.rules, config.priceCents, quantity?.value || 1, config.currency);
  root.querySelector('[data-fa-tier-wrap]').hidden = initial.tiers.length === 0;
  if (initial.tiers.length) {
    const tiers = [...initial.tiers];
    if (tiers[0].minQuantity > 1) tiers.unshift({ minQuantity: 1, priceCents: config.priceCents });
    tierList.replaceChildren(...tiers.map((tier, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fa-floetrol-tier';
      button.dataset.faTierMin = String(tier.minQuantity);
      const next = tiers[index + 1]?.minQuantity;
      const title = document.createElement('span');
      title.textContent = next ? labels.range.replace('{from}', tier.minQuantity).replace('{to}', next - 1) : labels.from.replace('{quantity}', tier.minQuantity);
      const price = document.createElement('strong'); price.textContent = money(tier.priceCents);
      const unit = document.createElement('small'); unit.textContent = labels.perContainer;
      button.append(title, price, unit);
      if (quantity?.max && tier.minQuantity > Number(quantity.max)) button.disabled = true;
      button.addEventListener('click', () => {
        quantity.value = String(tier.minQuantity);
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      });
      return button;
    }));
  }
  quantity?.addEventListener('input', refreshPrice);
  quantity?.addEventListener('change', refreshPrice);
  refreshPrice();

  root.querySelectorAll('product-form-component').forEach((component) => {
    const form = component.querySelector('form');
    const button = form?.querySelector('button[type="submit"]');
    const feedback = component.querySelector('[data-fa-form-feedback]');
    if (!form || !button || !feedback) return;
    let pending = false;
    let timeout;
    const finish = (message, isError = false) => {
      if (!pending) return;
      pending = false;
      clearTimeout(timeout);
      component.removeAttribute('aria-busy');
      button.disabled = false;
      feedback.textContent = message;
      feedback.classList.toggle('is-error', isError);
    };
    form.addEventListener('submit', (event) => {
      if (pending || button.disabled || !form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      pending = true;
      component.setAttribute('aria-busy', 'true');
      feedback.classList.remove('is-error');
      feedback.textContent = labels.adding;
      // Wait until the entire submit event has propagated. A microtask can run
      // between listeners and make Horizon's disabled-button guard abort.
      setTimeout(() => { if (pending) button.disabled = true; }, 0);
      timeout = setTimeout(() => finish(labels.unconfirmed, true), 25000);
    });
    component.addEventListener('cart:update', (event) => {
      if (event.target !== component) return;
      finish(event.detail?.data?.didError ? '' : labels.added, Boolean(event.detail?.data?.didError));
    });
    component.addEventListener('cart:error', (event) => {
      if (event.target === component) finish('', true);
    });
  });

  const sticky = root.querySelector('[data-fa-sticky]');
  root.querySelector('[data-fa-sticky-add]')?.addEventListener('click', () => {
    const form = mainForm.querySelector('form');
    if (!form.reportValidity()) {
      mainForm.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    const button = mainForm.querySelector('button[type="submit"]');
    if (!button.disabled) button.click();
  });
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => { sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0; }, { threshold: 0 });
    observer.observe(mainForm);
  }
  delete root.dataset.faInitError;
  root.dataset.faReady = 'true';
}

if (typeof document !== 'undefined') {
  const start = () => document.querySelectorAll('[data-fa-floetrol]').forEach(initFloetrol);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
}
